import React from 'react'
import { Col, Empty, Row, Typography } from 'antd'
import { neutralColors, spacing } from '@vacationtracker/shared/constants/ThemeSetting'
import { Button } from '../Button'

const { Title, Text } = Typography

export type EmptyStateVariant = 'compact' | 'full'

export interface EmptyStateAction {
  label: string
  onClick: () => void
}

export interface EmptyStateHelpDeskLink {
  text: string
  href: string
}

export interface EmptyStateProps {
  /** `compact` = icon + short text (replaces raw antd `Empty`). `full` = title/subtitle/CTA (replaces `EmptyData`). */
  variant?: EmptyStateVariant
  /** `compact` only. */
  description?: React.ReactNode
  /** `full` only. */
  title?: React.ReactNode
  /** `full` only. */
  subtitle?: React.ReactNode
  /** Rendered via the shared Button (`variant="primary"`): under the text in `compact`, beside the help link in `full`. */
  action?: EmptyStateAction
  /** `full` only. Renders a labeled video placeholder - no real embed logic. */
  videoId?: string
  /** `full` only. */
  helpDeskLink?: EmptyStateHelpDeskLink
}

/**
 * Vacation Tracker's single empty-state component. Merges the bare antd `<Empty>` usage
 * (compact) and the rich custom `EmptyData` component (full) behind one `variant` prop
 * (see docs/component-audit/empty-states.html) rather than picking one as a winner -
 * both serve real, different contexts (a table with no rows vs. a whole feature page).
 */
export function EmptyState({ variant = 'compact', description, title, subtitle, action, videoId, helpDeskLink }: EmptyStateProps) {
  if (variant === 'compact') {
    return (
      <Empty description={description}>
        {action && (
          <Button variant="primary" onClick={action.onClick}>
            {action.label}
          </Button>
        )}
      </Empty>
    )
  }

  return (
    <Row gutter={spacing.base}>
      <Col xs={24} xl={videoId ? 12 : 24}>
        {title && <Title level={4}>{title}</Title>}
        {subtitle && <Text type="secondary">{subtitle}</Text>}
        {(action || helpDeskLink) && (
          <div style={{ display: 'flex', gap: spacing.xs, marginTop: spacing.lg }}>
            {action && (
              <Button variant="primary" onClick={action.onClick}>
                {action.label}
              </Button>
            )}
            {helpDeskLink && (
              <Button variant="outline" href={helpDeskLink.href} target="_blank" rel="noopener noreferrer">
                {helpDeskLink.text}
              </Button>
            )}
          </div>
        )}
      </Col>
      {videoId && (
        <Col xs={24} xl={12}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              aspectRatio: '16 / 9',
              width: '100%',
              background: neutralColors.bgLayout,
              border: `1px solid ${neutralColors.border}`,
              borderRadius: 8,
              color: neutralColors.textSecondary,
              fontSize: 13,
              textAlign: 'center',
              padding: spacing.sm,
            }}
          >
            ▶ Video placeholder ({videoId})
          </div>
        </Col>
      )}
    </Row>
  )
}

export default EmptyState
